import cx from 'classnames'
import {
  formatLocalIsoDate,
  formatUtcIsoDate,
  isIsoDateString,
  parseISO,
  parseIsoDateAsUtc,
} from '@pogokid/util/datetime'
import { toZonedTime } from '@pogokid/util/tz'
import { TimeHTMLAttributes } from 'react'
import styles from './DateTile.module.css'

export type DateTileSize = 'small' | 'medium' | 'large'
export type DateTileState = 'default' | 'highlighted' | 'past'
export type DateTileTopLine = 'weekday' | 'month'

export interface DateTileProps
  extends Omit<
    TimeHTMLAttributes<HTMLElement>,
    'dateTime' | 'children'
  > {
  /**
   * The date or time to display as an ISO
   * string
   *
   * eg: 2020-01-01T10:00
   */
  isoDateTime: string
  /**
   * The IANA timezone the day is worked out in, eg: Europe/London.
   * Defaults to the browser's timezone.
   *
   * Ignored when `isoDateTime` is a date without a time,
   * as that already names the calendar day.
   */
  timeZone?: string
  /** The size of the square */
  size?: DateTileSize
  /** The visual state of the tile */
  state?: DateTileState
  /** What to show above the day number */
  topLine?: DateTileTopLine
  locale?: string
}

export function DateTile({
  isoDateTime,
  timeZone,
  size = 'medium',
  state = 'default',
  topLine = 'weekday',
  locale = 'en-GB',
  className,
  ...props
}: DateTileProps) {
  const isDateOnly = isIsoDateString(isoDateTime)
  const date = isDateOnly
    ? parseIsoDateAsUtc(isoDateTime)
    : parseISO(isoDateTime)
  const zone = isDateOnly ? 'UTC' : timeZone

  const part = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, {
      ...options,
      timeZone: zone,
    }).format(date)

  // The calendar date in the timezone, the form `datetime` expects
  const calendarDate = isDateOnly
    ? formatUtcIsoDate(date)
    : formatLocalIsoDate(zone ? toZonedTime(date, zone) : date)

  return (
    <time
      dateTime={calendarDate}
      className={cx(styles.DateTile, className, {
        [styles.isSizeSmall]: size === 'small',
        [styles.isSizeLarge]: size === 'large',
        [styles.isHighlighted]: state === 'highlighted',
        [styles.isPast]: state === 'past',
      })}
      {...props}
    >
      <span className={styles.DateTile__label}>
        {part({ dateStyle: 'full' })}
      </span>
      <span
        className={styles.DateTile__topLine}
        aria-hidden="true"
      >
        {part(
          topLine === 'month'
            ? { month: 'short' }
            : { weekday: 'short' }
        )}
      </span>
      <span className={styles.DateTile__day} aria-hidden="true">
        {part({ day: 'numeric' })}
      </span>
    </time>
  )
}
